import _ from 'lodash';
import { CourseDetail, CourseGroup, Dish, FirstMenu, MenuDish, SecondMenu } from '../../../models';
import http, { BASE_URL_2, BASE_URL_3, OkPacket, ResponseErrorType, WillStopConfig } from '../../http';
import { getRBPStringId } from '../../../page/main/main-page-states';
import { CancelToken } from 'axios';


const menuApi = {
    /**
     * 获取菜单数据  v3
     */
    async get_menus_v3(params: GetMenusParams, rbpStringId?: string): Promise<FirstMenu[]> {
        const sid = (rbpStringId && rbpStringId.trim()) || getRBPStringId();
        return await http.get(`${BASE_URL_3}/menus/${sid}`,
            { params, stop_by_unmount: true } as WillStopConfig)
    },

    /**
     * 获取菜单的菜品数据  v3
     */
    async get_menu_item_v3(params: { name?: string, id?: number, name2?: string, pag_page?: number, pag_per_page?: number }, config: { cancelToken: CancelToken }): Promise<MenuDish[]> {
        return await http.get(`${BASE_URL_3}/menu_item/${getRBPStringId()}`,
            { params, ...config })
    },
    /**
     * 获取所有菜单菜品的 item_id 数据  v3
     */
    async get_menu_item_id_v3(): Promise<MenuDish[]> {
        return await http.get(`${BASE_URL_3}/menu_item_id/${getRBPStringId()}`)
    },
    /**
     * 获取二级菜单数据  v3
     * @param params.id 二级菜单id
     * @param params.with_course 传1: 不显示“套餐”菜品；不传显示全部（用于套餐添加菜单时，去除套餐类型的菜品）
     */
    async get_second_menus_v3(params: { id: number, with_course?: number }): Promise<SecondMenu[]> {
        return await http.get(`${BASE_URL_3}/menus/second/${params.id}/${getRBPStringId()}`)
    },

    /**
     * 删除一级菜单图片
     * 大图小图
     * @param firstMenuId
     * @returns
     */
    async delete_first_menu_image(firstMenuId: number): Promise<any> {
        const formData = new FormData();
        formData.append('imageName', `${firstMenuId}`);
        return await http.delete(`${BASE_URL_3}/image/firstMenu/${getRBPStringId()}`, {
            data: formData,
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })
    },

    /**
     * 修改二级菜单
     * @param id
     * @param params 二级菜单数据
     */
    async patch_second_menu(id: number, params: SecondMenu, rbpStringId?: string): Promise<any> {
        const sid = (rbpStringId && rbpStringId.trim()) || getRBPStringId();
        return await http.patch(`${BASE_URL_3}/menus/level/second/${id}/${sid}`,
            params
        )
    },

    /**
     * 修改一级菜单
     * @param firstMenu
     * @returns
     */
    async patch_first_menu(firstMenu: FirstMenu, rbpStringId?: string): Promise<OkPacket> {
        /** 名称与附加费用为固定字段；旧版按日价格关闭时成人/儿童/其他标价可走同一路径 */
        const {
            main_group_name,
            main_group_name_i18n,
            max_limit,
            extra_child,
            extra_normal,
            extra_other,
            normal_price,
            child_price,
            other_price,
        } = firstMenu as any;
        const sid = (rbpStringId && rbpStringId.trim()) || getRBPStringId();
        const body: Record<string, unknown> = {
            main_group_name,
            extra_child,
            extra_normal,
            extra_other,
        };
        if (main_group_name_i18n !== undefined) {
            body.main_group_name_i18n = main_group_name_i18n;
        }
        if (max_limit !== undefined && max_limit !== null) {
            body.max_limit = max_limit;
        }
        if (normal_price !== undefined) {
            body.normal_price = normal_price;
        }
        if (child_price !== undefined) {
            body.child_price = child_price;
        }
        if (other_price !== undefined) {
            body.other_price = other_price;
        }
        return await http.patch(`${BASE_URL_3}/menus/level/first/${firstMenu.main_group_id}/${sid}`, body)
    },

    /**
     * 批量修改一级菜单搜索规则
     * @param firstMenu
     * @returns
     */
    async patch_first_menu_search_rules(param: Array<{ exact_search_rules: number | null, main_group_id: number }>): Promise<OkPacket> {
        return await http.patch(`${BASE_URL_2}/menus/level/first/${getRBPStringId()}`, {
            menus: param
        })
    },

    /**
     * 修改一级菜单每日价格
     * @param dishes
     * @returns
     */
    async patch_first_menu_day_price(firstMenu: FirstMenu, rbpStringId?: string): Promise<OkPacket> {
        const { main_group_id, value, main_group_name } = firstMenu as any;
        const sid = (rbpStringId && rbpStringId.trim()) || getRBPStringId();
        return await http.patch(`${BASE_URL_3}/menus/level/first/${sid}?main_group_id=${main_group_id}&main_group_name=${main_group_name}`,
            value
        )
    },

    /**
     * 批量修改菜品
     * @param dishes
     * @param is_patch_not_limit 只修改 "不受轮次限制"
     */
    async patch_dishes(dishes: PatchDishesParams[], is_patch_not_limit?: boolean, rbpStringId?: string): Promise<OkPacket> {
        const sid = (rbpStringId && rbpStringId.trim()) || getRBPStringId();
        return await http.patch(`${BASE_URL_3}/dishes/${sid}`,
            { dishes, is_patch_not_limit }
        )
    },

    /**
     * 新增一级菜单
     * @param params
     */
    async post_create_first_menu(params: FirstMenu, rbpStringId?: string): Promise<OkPacket> {
        const sid = (rbpStringId && rbpStringId.trim()) || getRBPStringId();
        return await http.post(`${BASE_URL_3}/menus/level/first/${sid}`,
            params
        )
    },

    /**
     * 删除一级菜单
     * @param id 一级菜单id
     */
    async delete_first_menu(id: number, rbpStringId?: string): Promise<OkPacket> {
        const sid = (rbpStringId && rbpStringId.trim()) || getRBPStringId();
        return await http.delete(`${BASE_URL_3}/menus/level/first/${id}/${sid}`)
    },

    /**
     * 新增二级菜单
     * @param id 一级菜单id
     * @param params
     */
    async post_create_second_menu(id: number, params: SecondMenu, rbpStringId?: string): Promise<OkPacket> {
        const sid = (rbpStringId && rbpStringId.trim()) || getRBPStringId();
        return await http.post(`${BASE_URL_3}/menus/level/second/${id}/${sid}`,
            params
        )
    },

    /**
     * 删除二级菜单
     * @param id 二级菜单id
     * @param first_menu_id 二级菜单所属的一级菜单id
     */
    async delete_second_menu(id: number, first_menu_id: number, rbpStringId?: string): Promise<OkPacket> {
        const sid = (rbpStringId && rbpStringId.trim()) || getRBPStringId();
        return await http.delete(`${BASE_URL_3}/menus/level/second/${id}/${sid}`,
            { data: { first_menu_id } }
        )
    },

    /**
     * 修改一级菜单位置
     * @param firstMenuArr
     */
    async patch_change_first_menu_index(firstMenuArr: FirstMenu[]) {
        return await http.patch(`${BASE_URL_3}/menus/first/index/${getRBPStringId()}`,
            firstMenuArr
        )
    },

    /**
     * 一级菜单下菜品排序（与 `PATCH /api/v3/menus/first/index/{rbpId}` 一致；body：`{ item_id, slu_priority }[]`）
     * @param items 每项 `slu_priority` 为展示顺序权重（通常从 1 连续）
     */
    async patch_menus_first_index_dish_priorities(
        items: Array<{ item_id: number; slu_priority: number }>,
        rbpStringId?: string,
    ): Promise<OkPacket> {
        const sid = (rbpStringId && rbpStringId.trim()) || getRBPStringId();
        return await http.patch(`${BASE_URL_3}/menus/first/index/${sid}`, items);
    },

    /**
     * 同一菜品下套餐组排序（`PATCH /v3/menus/first/index/{rbpId}`；body：`{ course_group_id, slu_priority }[]`）
     */
    async patch_menus_first_index_course_group_priorities(
        items: Array<{ course_group_id: number; slu_priority: number }>,
        rbpStringId?: string,
    ): Promise<OkPacket> {
        const sid = (rbpStringId && rbpStringId.trim()) || getRBPStringId();
        return await http.patch(`${BASE_URL_3}/menus/first/index/${sid}`, items);
    },

    /**
     * 修改二级菜单位置
     * @param secondMenuArr
     */
    async patch_change_second_menu_index(secondMenuArr: SecondMenu[], rbpStringId?: string) {
        const sid = (rbpStringId && rbpStringId.trim()) || getRBPStringId();
        return await http.patch(`${BASE_URL_3}/menus/second/index/${sid}`,
            secondMenuArr
        )
    },

    /**
     * 上传菜品图片
     * @param dishId `${dishId}_s,${dishId}`
     * @param imageFile
     * @returns
     */
    async post_dishes_image(dishId: string, imageFile: File, item_ids?: number[], isGenSmall?: string, isBatch?: string, imageSmall?: File): Promise<any> {
        const formData = new FormData();
        formData.append('item_id', dishId);
        formData.append('image', imageFile);
        formData.append('isGenSmall', isGenSmall || '0');
        formData.append('isBatch', isBatch || '0');
        formData.append('item_ids', JSON.stringify(item_ids));
        imageSmall && formData.append('image_s', imageSmall);
        return await http.post(`${BASE_URL_3}/dishes/image/${getRBPStringId()}`, formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
    },

    /**
     * 新建菜品
     * @param dish
     * @returns
     */
    async post_dishes(dish: PatchDishesParams, rbpStringId?: string): Promise<OkPacket> {
        const sid = (rbpStringId && rbpStringId.trim()) || getRBPStringId();
        return await http.post(`${BASE_URL_3}/dishes/${sid}`, dish)
    },

    /**
     * 删除菜品图片
     * 大图小图
     * @param dishId
     * @returns
     */
    async delete_dishes_image(dishId: number | string): Promise<boolean> {
        const result = await Promise.all([
            http.delete(`${BASE_URL_3}/dishes/image/${dishId}_s/${getRBPStringId()}`),
            http.delete(`${BASE_URL_3}/dishes/image/${dishId}/${getRBPStringId()}`)
        ]).then((res) => {
            const isError = res.some((item) => _.get(item, 'error'))
            return isError ? false : true
        }).catch(() => {
            return false
        })
        return result

    },

    /**
     * 设置额外费用
     * @returns
     */
    async patch_settings_1(menuId: number): Promise<{ details: OkPacket, ok: boolean }> {
        return http.patch(`${BASE_URL_3}/settings/1/${getRBPStringId()}`, {
            init: true,
            menu_id: menuId
        })
    },

    /**
     * 批量复制菜品
     * @param ids     菜品ids
     * @param slu_ids 二级菜单ids
     */
    async patch_copy_dishes(ids: number[], slu_ids: number[], rbpStringId?: string): Promise<{ data: OkPacket[], insert_menu_item: Dish[] }> {
        const sid = (rbpStringId && rbpStringId.trim()) || getRBPStringId();
        return await http.patch(`${BASE_URL_3}/CopyDishes/${sid}`, {
            ids,
            slu_ids,
        })
    },

    /**
     * 批量删除菜品
     * @param ids 菜品ids
     * @param rbpStringId 餐厅码（菜单库等与全局 RBP 不一致时传入）
     */
    async delete_dishes(ids: { item_id: number; slu_id: number }[], rbpStringId?: string): Promise<OkPacket> {
        const sid = (rbpStringId && rbpStringId.trim()) || getRBPStringId();
        return await http.delete(`${BASE_URL_3}/dishes/${sid}`, { data: [...ids] });
    },

    /**
     * 查询菜品的所属菜单 不同菜单的统一菜品
     * @returns
     */
    async get_dishes(dishId: number): Promise<MenuDish[]> {
        return await http.get(`${BASE_URL_3}/dishes/${getRBPStringId()}`, {
            params: {
                id: dishId
            }
        })
    },

    /**
     * 创建套餐区域
     * @returns
     */
    async post_course_group(id: number, params: CourseGroup, rbpStringId?: string): Promise<OkPacket> {
        const sid = (rbpStringId && rbpStringId.trim()) || getRBPStringId();
        return await http.post(`${BASE_URL_3}/course_group/${id}/${sid}`, params)

    },

    /**
     * 获取套餐区域
     * @returns
     */
    async get_dish_course(): Promise<CourseGroup[]> {
        return await http.get(`${BASE_URL_3}/dish/course/${getRBPStringId()}`, {
            params: {
                is_origin_name1: true
            }
        })
    },
    /**
     * 修改套餐设置是否必选满
     * @returns
     */
    async patchMenuItemFullSelect(params: any, rbpStringId?: string): Promise<CourseGroup[]> {
        const sid = (rbpStringId && rbpStringId.trim()) || getRBPStringId();
        return await http.patch(`${BASE_URL_3}/menu_item_full_select/${sid}`, params)
    },

    /**
     * 修改套餐区域
     * @param dishId 菜品id
     * @returns
     */
    async patch_course_group(dishId: number, courseGroupId: number, data: CourseGroup, rbpStringId?: string): Promise<OkPacket> {
        const sid = (rbpStringId && rbpStringId.trim()) || getRBPStringId();
        return await http.patch(`${BASE_URL_3}/course_group/${dishId}/${sid}`,
            data,
            {
                params: { course_group_id: courseGroupId }
            },
        )
    },

    /**
     * 删除套餐区域（套餐组）
     * @param ids 套餐组 id 列表
     * @param rbpStringId 餐厅码（菜单库模块内所选店与全局不一致时传入）
     * @returns 删除结果
     */
    async delete_course_group(ids: number[], rbpStringId?: string): Promise<OkPacket> {
        const sid = (rbpStringId && rbpStringId.trim()) || getRBPStringId();
        return await http.delete(`${BASE_URL_3}/course_group/${sid}`, {
            params: { id: ids }
        })
    },

    /**
     * 创建套餐详情
     * @param id 套餐区域id
     * @returns
     */
    async post_course_detail(id: number, params: CourseDetail[], rbpStringId?: string): Promise<OkPacket> {
        const sid = (rbpStringId && rbpStringId.trim()) || getRBPStringId();
        return await http.post(`${BASE_URL_3}/course_detail/${id}/${sid}`, params)
    },

    /**
     * 修改套餐详情
     * @param id 套餐区域id
     * @returns
     */
    async patch_course_detail(id: number, params: CourseDetail[], rbpStringId?: string): Promise<OkPacket> {
        const sid = (rbpStringId && rbpStringId.trim()) || getRBPStringId();
        return await http.patch(`${BASE_URL_3}/course_detail/${id}/${sid}`, params)
    },

    /**
     * 删除套餐详情
     * @param id 套餐区域id
     * @returns
     */
    async delete_course_detail(ids: number[], rbpStringId?: string): Promise<OkPacket> {
        const sid = (rbpStringId && rbpStringId.trim()) || getRBPStringId();
        return await http.delete(`${BASE_URL_3}/course_detail/${sid}`, {
            data: ids
        })
    },

    /**
     * 获取菜品显示名称
     */
    async get_dish_display_name(): Promise<Array<{
        id: number;
        name: string;
        value: { showItemName1: boolean; }
    }>> {
        return await http.get(`${BASE_URL_3}/settings/21`);
    },

    /**
     * 获取点餐菜品排序
     */
    async get_dish_sort(): Promise<Array<{
        id: number;
        name: string;
        value: 'item_id ASC' | 'item_name1 ASC';
    }>> {
        return await http.get(`${BASE_URL_3}/settings/22`);
    },

    /**
     * 修改点餐菜品排序
     */
    async patch_dish_display_name(isShowDishName1: boolean): Promise<{
        ok: boolean;
        detail: object;
    }> {
        return await http.patch(`${BASE_URL_3}/settings/21/${getRBPStringId()}`, {
            showItemName1: isShowDishName1
        });
    },

    /**
     * 修改点餐菜品排序
     */
    async patch_dish_sort(value: string): Promise<{
        ok: boolean;
        detail: object;
    }> {
        return await http.patch(`${BASE_URL_3}/settings/22/${getRBPStringId()}`, {
            stringValue: value
        });
    },
    /**
     * 获取数字区域的菜品
     */
    async get_id_area_dish(id_area: string[], menus_ids: string[]): Promise<any> {
        return await http.get(`${BASE_URL_3}/dishes/idArea/${getRBPStringId()}`, {
            params: {
                id_area,
                menus_ids
            },

        });
    },

    /**
     * 获取批量上传文件夹图片
     */
    async get_upload_file_imgs(): Promise<any> {
        return await http.get(`${BASE_URL_3}/dishes/uploadImage`);
    },

    /**
     * 批量上传菜品图片
     */
    async post_batch_dishes_img(image_name: string, item_id: string): Promise<any> {
        return await http.post(`${BASE_URL_3}/dishes/batchimage/${getRBPStringId()}`, {
            image_name,
            item_id,
        });
    },

    /**
     * 获取退菜设置
     */
    async get_return_reason(): Promise<any> {
        return await http.get(`${BASE_URL_3}/dishes/reason_return/${getRBPStringId()}`)
    },

    /**
     * 删除退菜设置
     */
    async del_reason_return(id: number): Promise<OkPacket> {
        return await http.delete(`${BASE_URL_3}/dishes/reason_return/${id}/${getRBPStringId()}`)
    },

    /**
     * 批量删除退菜设置
     */
    async mul_del_reason_return(ids: number[]): Promise<OkPacket> {
        return await http.delete(`${BASE_URL_3}/dishes/batch/reason_return/${getRBPStringId()}`, { data: { ids } })
    },

    /**
     * 添加退菜设置
     */
    async post_reason_return(params: any): Promise<any> {
        return await http.post(`${BASE_URL_3}/dishes/reason_return/${getRBPStringId()}`, params)
    },

    /**
     * 修改退菜设置
     */
    async patch_reason_return(params: any): Promise<any> {
        return await http.patch(`${BASE_URL_3}/dishes/reason_return/${getRBPStringId()}`, params)
    },
    /*
     * 复制一级菜单大类
     */
    async patch_copy_menu(params: { id: number, name: string }): Promise<any> {
        const { id, name } = params
        return await http.patch(`${BASE_URL_3}/CopyMenus/${id}/${getRBPStringId()}`, {
            name,
        });
    },
    /*
     * 复制二级菜单大类
     */
    async patch_copy_second_menu(params: { id: number, main_group_id: number, name: string }): Promise<any> {
        const { id, name, main_group_id } = params
        return await http.patch(`${BASE_URL_3}/CopySlu/${id}/${getRBPStringId()}?main_group_id=${main_group_id}`, {
            name,
        });
    },
    /**
     * 获取基础菜品库
     */
    async get_classify_menu_item(): Promise<any> {
        return await http.get(`${BASE_URL_3}/classify_menu_item/${getRBPStringId()}`);
    },

    /**
     * 获取菜品库
     */
    async get_menu_library(): Promise<any> {
        return await http.get(`${BASE_URL_3}/menu_library/${getRBPStringId()}`);
    },

    /**
     * 批量操作基础菜品到菜单
     * @param param
     */
    async patch_menu_library_to_menus(param: { menus_arr: Array<MenuDish & { new_ids?: string }>, slu_ids: number[] }): Promise<any> {
        return await http.post(`${BASE_URL_3}/slu_menu_library/${getRBPStringId()}`, param);
    },

    /**
     * 批量创建菜品库
     * @param dishes
     */
    async post_menu_library(dishes: PatchDishesParams[]): Promise<any> {
        return await http.post(`${BASE_URL_3}/menu_library/${getRBPStringId()}`, dishes);
    },

    /**
     * 修改菜品库
     */
    async patch_menu_library(dishes: PatchDishesParams, isUpdate: boolean): Promise<any> {
        const { id, ...body } = dishes
        return await http.patch(`${BASE_URL_3}/menu_library/${id}/${getRBPStringId()}?update=${isUpdate}`, { ...body });
    },

    /**
     * 删除菜品库
     */
    async delete_menu_library(dishIds: number[]): Promise<any> {
        return await http.delete(`${BASE_URL_3}/menu_library/${getRBPStringId()}`, { data: dishIds });
    },

    /**
     * 批量上传菜品
     * @param data
     * @returns
     */
    async post_menu_item_excel(data: PostMenuItemExcelParams): Promise<OkPacket> {
        return await http.post(`${BASE_URL_3}/menu_item/excel/${getRBPStringId()}`, { data })
    },

    /**
     * 批量导入菜品库数据
     */
    async post_excel_menu_library(data: PostExcelMenuLibraryParams): Promise<OkPacket> {
        return await http.post(`${BASE_URL_3}/excel/menu_library/${getRBPStringId()}`, data)
    },

    /**
     * 清空菜单菜品相关数据
     */
    async delete_menus_dish_data(): Promise<OkPacket> {
        return await http.delete(`${BASE_URL_3}/menus/${getRBPStringId()}`);
    },

    /**
     * 清空菜单菜品相关数据
     */
    async delete_first_menus_dish_image_data(): Promise<OkPacket | ResponseErrorType> {
        return await http.delete(`${BASE_URL_3}/image/menus/${getRBPStringId()}`);
    },
    /**
      * 获取套餐区域
      * @returns
      */
    async get_dish_course_by_id(id: number, rbpStringId?: string): Promise<CourseGroup[]> {
        const sid = (rbpStringId && rbpStringId.trim()) || getRBPStringId();
        return await http.get(`${BASE_URL_3}/dish/course/${sid}`, {
            params: {
                is_origin_name1: true,
                course_group_id: id,
            }
        })
    },


    /**
     * 复制套餐组
     */
    async copy_course_detail(params: {
        course_id: number, // 目标套餐头id
        course_group_id: number //要复制的套餐组id
    }[]
    ): Promise<OkPacket> {
        return await http.patch(`${BASE_URL_3}/copy_course_detail/${getRBPStringId()}`, params)
    },

    /**
     * 转移套餐组
     */
    async transfer_course_detail(params: {
        course_id: number, // 目标套餐头id
        course_group_id: number //要复制的套餐组id
    }[]
    ): Promise<OkPacket> {
        return await http.patch(`${BASE_URL_3}/move_course_detail/${getRBPStringId()}`, params)
    },


}
export default menuApi;

// get_menus 传参类型
type GetMenusParams = {
    //0：普通菜单 1：额外费用 不传即两种类型菜单都有
    type?: number;
    // 是否获取菜单和相应的菜品F
    with_dish?: boolean;
    // 菜品字段名 ASC/DESC 'item_name1 ASC'
    sort?: string;
    // 1 检查是否带有图片
    get_image_status?: number;
    // 0 是跟其他项目一致排序，1是拖拽排序
    rbpSort?: number;
    // 一级菜单 传main_group_id时 则rbpSort type 不传
    main_group_id?: number;
}
// patch_dishes 传参类型
export type PatchDishesParams = Omit<MenuDish, 'allergen' | 'other_detail_info'> & {
    allergen?: string;
    other_detail_info?: string;
    required_condiment?: number;
    item_ids?: string;
    new_ids?: string;
    print_class?: number
}

// 批量上传菜品参数
export type PostMenuItemExcelParams = {
    // 菜单菜品
    menuItems: MenuItem[];
    // 调味品
    condimentData: CondimentData[];
}

// 菜单菜品
type MenuItem = {
    main_group_name: string;
    main_group_id: number;
    second_group_id: number;
    secondData: SecondData[];
    // 用于导入大类排序的值
    main_menu_index: number;
}

// 二级菜单
type SecondData = {
    dmi_slu_id: number;
    dmi_slu_name: string;
    menuData: MenuDish[];
    // 用于导入小类排序的值
    dmi_slu_index?: number;
}
// 调味品组
type CondimentData = {
    scomdiment_groups_id: number;
    comdiment_groups_name: string;
    comdiments: Condiment[]
}
// 调味品
type Condiment = {
    // 二级菜单id
    id: number;
    item_id: number;
    item_name1: string;
    item_name2: string;
    price_1: number;
    unit_1: string;
}

// 导入基本菜品库参数
export type PostExcelMenuLibraryParams = {
    menus: MenuDish[]
}
